import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import useSandbox from '../hooks/useSandbox';
import useChatFeature from '../hooks/useChatFeature';
import ConnectToolModal from '../components/sandbox/ConnectToolModal';
import { PlanChangeButton, PlanPill, usePlanRequests } from '../components/sandbox/PlanRequest';
import { SandboxTypePill } from '../components/sandbox/SandboxTypePicker';
import { useMySandboxStats } from '../components/sandbox/MySandboxStats';
import UsageCard from '../components/sandbox/UsageCard';
import RunningNow from '../components/sandbox/RunningNow';
import { SandboxStatePill, SandboxSummary } from '../components/sandbox/SandboxSections';
import { ManageSandboxMenu } from '../components/sandbox/ManageSandbox';
import SandboxWaking from '../components/sandbox/SandboxWaking';
import { getSettings, updateSettings } from '../services/SettingsService';
import GettingStarted from '../components/sandbox/GettingStarted';
import NewProjectDialog from '../components/sandbox/NewProjectDialog';
import { isRunning, sleepSandbox, stopService, wakeSandbox } from '../services/SandboxService';
import { includedConnections } from '../components/sandbox/connections';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import miaFlowAuth from '../auth/MiaFlowAuth';
import { projectHomeUrl } from '../utils/openProject';
import LiveNow from '../components/collab/LiveNow';
import SinceYouLeft from '../components/collab/SinceYouLeft';
import { formatRelativeTime } from '../utils/relativeTime';
import { useLiveRefresh } from '../utils/livePoll';
import VendorName from '../components/sandbox/VendorName';
import '../components/sandbox/sandbox.css';
import './SandboxHome.css';

/**
 * Home in the sandbox-first experience (docs/SANDBOX_FIRST.md). ONE Home, never a toggle between two:
 * - Chat off: the person's sandbox, like "check my machine" — state and plan, one usage card (meters, the AI tools,
 *   details behind a disclosure), what runs now (services, then every process behind a disclosure), the sandbox's
 *   recent projects, and one line of more things to do with Flow.
 * - Chat on (`renderChatHome`, App.jsx's prompt Home): the prompt people know ("Ready to flow", chips, Continue) on top
 *   and the sandbox below it, compact (SandboxSummary). Arriving with `state.focusPrompt` (Chat just turned on from
 *   Features › Chat or the first run) puts the cursor in the prompt.
 */
export default function SandboxHome({ user = null, selectedTenant = null, onNewProject, renderChatHome = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const focusPrompt = Boolean(location.state?.focusPrompt);
  const { sandbox, error, loading, refresh } = useSandbox();
  const [chatOn, , chatAvailable] = useChatFeature();
  const [tool, setTool] = useState(null);
  // Ask for another plan (the sandbox admin decides): the person's plans, pending request and last decisions.
  const [planRequests, setPlanRequests] = usePlanRequests();
  // The person's own stats (active hours, disks, release, events), the admin's view of them cut to what is theirs.
  const [myStats] = useMySandboxStats();
  // "+ New project" opens the one-field dialog right here (a skeleton project in the sandbox, then Code).
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const startNewProject = onNewProject || (() => setNewProjectOpen(true));
  const [recent, setRecent] = useState([]);
  const [stoppingAll, setStoppingAll] = useState(false);
  // Put to sleep: only a per-person sandbox can; a 409 (shared mode) or 404 (older vfs-service) hides the button.
  const [sleeping, setSleeping] = useState(false);
  const [sleepError, setSleepError] = useState(null);
  const [sleepUnsupported, setSleepUnsupported] = useState(false);
  const tenantId = selectedTenant?.tenantId || selectedTenant?.id
    || miaFlowAuth.getSelectedTenant()?.tenantId || miaFlowAuth.getSelectedTenant()?.id;
  const firstName = (user?.name || user?.given_name || '').split(' ')[0];
  // The first answer can take a few seconds while an asleep sandbox wakes up: say so instead of showing nothing.
  const [waking, setWaking] = useState(false);
  useEffect(() => {
    if (!loading) { setWaking(false); return undefined; }
    const timer = setTimeout(() => setWaking(true), 800);
    return () => clearTimeout(timer);
  }, [loading]);

  // The person's settings: someone who never finished the first-run setup starts there (docs/SANDBOX_FIRST.md); the
  // getting-started checklist reads its steps and whether it was hidden.
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = (first) => getSettings()
      .then((value) => {
        if (cancelled || !value) { return; }
        setSettings(value);
        if (first && value.onboarding?.completed === false) { navigate('/welcome', { replace: true }); }
      })
      .catch(() => {});
    load(true);
    const onChange = () => load(false);
    window.addEventListener('flow:settings-changed', onChange);
    return () => { cancelled = true; window.removeEventListener('flow:settings-changed', onChange); };
  }, [navigate]);

  const hideChecklist = () => {
    setSettings((current) => (current ? { ...current, home: { ...current.home, checklistDismissed: true } } : current));
    updateSettings({ home: { checklistDismissed: true } }).catch(() => {});
  };
  const checklistAction = (step) => {
    if (step === 'tool') { navigate('/welcome'); }
    if (step === 'project') { startNewProject(); }
    if (step === 'preview') { if (recent[0]) { openProject(recent[0]._id); } else { navigate('/projects'); } }
    if (step === 'chat') { navigate(chatOn ? '/chat' : '/features/chat'); }
    if (step === 'share') { if (recent[0]) { openProject(recent[0]._id); } else { navigate('/projects'); } }
  };

  // The sandbox polls itself (useSandbox, every 10 s); Recent projects reload every 30 s and on Refresh.
  const [recentTick, setRecentTick] = useState(0);
  useLiveRefresh(() => setRecentTick((tick) => tick + 1), { intervalMs: 30000, enabled: Boolean(tenantId) });
  const [refreshing, setRefreshing] = useState(false);
  const refreshAll = async() => {
    setRefreshing(true);
    setRecentTick((tick) => tick + 1);
    try { await refresh(); } finally { setRefreshing(false); }
  };

  useEffect(() => {
    let cancelled = false;
    if (!tenantId) { setRecent([]); return undefined; }
    miaPlatformDataService.getProjects(tenantId, null, 1, 20)
      .then((result) => {
        const sorted = [...(result?.data || [])].sort((a, b) => (
          new Date(b.visitedAt || b.updatedAt || b.createdAt || 0) - new Date(a.visitedAt || a.updatedAt || a.createdAt || 0)
        ));
        if (!cancelled) { setRecent(sorted.slice(0, 3)); }
      })
      .catch(() => { if (!cancelled && recentTick === 0) { setRecent([]); } });
    return () => { cancelled = true; };
  }, [tenantId, recentTick]);

  useEffect(() => {
    const onDeleted = (event) => {
      const id = event?.detail?.projectId;
      if (id) { setRecent((prev) => prev.filter((p) => p._id !== id)); setRecentTick((tick) => tick + 1); }
    };
    window.addEventListener('flow:project-deleted', onDeleted);
    return () => window.removeEventListener('flow:project-deleted', onDeleted);
  }, []);

  const services = useMemo(() => sandbox?.services || [], [sandbox]);
  const runningByProject = useMemo(() => services.reduce((acc, service) => {
    if (isRunning(service)) { acc[service.projectId] = (acc[service.projectId] || 0) + 1; }
    return acc;
  }, {}), [services]);
  const warnings = sandbox?.warnings || [];

  const openProject = (projectId, { history } = {}) => navigate(`${projectHomeUrl(tenantId, projectId)}${history ? `&history=${encodeURIComponent(history)}` : ''}`);
  // "Live now" says "for you" for the person's own agents (presence names a person by their sub).
  const liveYou = user?.sub ? { id: user.sub } : null;
  const openLogs = (projectId, serviceName) => navigate(`${projectHomeUrl(tenantId, projectId).replace('view=dashboard', 'view=logs')}&service=${encodeURIComponent(serviceName)}`);
  const stopAll = async(running) => {
    setStoppingAll(true);
    await Promise.allSettled(running.filter(isRunning).map((service) => stopService(service.sessionId)));
    setStoppingAll(false);
    refresh();
  };

  const putToSleep = async() => {
    setSleeping(true);
    setSleepError(null);
    try {
      await sleepSandbox();
      // useSandbox now reads the state, which never wakes it (the overview would).
      await refresh();
    } catch (err) {
      if (err.status === 409 || err.status === 404) { setSleepUnsupported(true); } else { setSleepError(err.message); }
    } finally {
      setSleeping(false);
    }
  };
  const connectionsIncluded = includedConnections(sandbox);
  // More with Flow's links: real links (open in a new tab works), followed in the app on a plain click.
  const go = (to) => (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) { return; }
    event.preventDefault();
    navigate(to);
  };

  const state = sandbox?.state || (error ? (error.status === 401 ? 'signed-out' : 'unreachable') : 'running');
  const sleepButton = sandbox?.state === 'running' && sandbox?.mode === 'per-person' && !sleepUnsupported
    ? <Button loading={sleeping} onClick={putToSleep}>Put to sleep</Button>
    : null;
  const [wakingUp, setWakingUp] = useState(false);
  const wakeUp = async() => {
    setWakingUp(true);
    setSleepError(null);
    try {
      await wakeSandbox();
      await refresh();
    } catch (err) {
      setSleepError(err.message);
    } finally {
      setWakingUp(false);
    }
  };
  const wakeButton = sandbox?.state === 'asleep' && sandbox?.mode === 'per-person'
    ? <Button loading={wakingUp} onClick={wakeUp}>Wake up</Button>
    : null;
  const refreshButton = (
    <Button aria-label="Refresh your sandbox" data-testid="sandbox-home-refresh" loading={refreshing} onClick={refreshAll} title="Refresh the usage, what runs and your recent projects (it also refreshes by itself)">
      Refresh
    </Button>
  );
  const modals = (
    <>
      <ConnectToolModal onClose={() => setTool(null)} tool={tool} />
      <NewProjectDialog onClose={() => setNewProjectOpen(false)} open={newProjectOpen} selectedTenant={selectedTenant} />
    </>
  );

  if (chatOn && renderChatHome) {
    return (
      <>
        {renderChatHome({
          focusPrompt,
          liveNow: (
            <>
              <LiveNow onOpenProject={openProject} you={liveYou} />
              <SinceYouLeft onOpenProject={openProject} projects={recent} you={liveYou} />
            </>
          ),
          sandboxSummary: (
            <>
              <SandboxSummary
              actions={<div className="sbx-home-actions">{refreshButton}{wakeButton}<ManageSandboxMenu onChanged={refresh} />{sleepError && <span className="sbx-action-error" role="alert">{sleepError}</span>}</div>}
              error={error}
              loading={loading}
              onChanged={refresh}
              onConnect={() => navigate('/welcome')}
              onOpenLogs={openLogs}
              onOpenProject={openProject}
              onOpenTool={setTool}
              onRetry={refresh}
              onStopAll={stopAll}
              recent={recent}
              sandbox={sandbox}
              sleepAction={sleepButton}
              state={state}
              stoppingAll={stoppingAll}
              tenantId={tenantId}
              waking={waking}
              warnings={warnings}
            />
            </>
          ),
        })}
        {modals}
      </>
    );
  }

  return (
    <div className="sbx-home">
      <div className="sbx-home-inner">
        <header className="sbx-home-header">
          <div className="sbx-home-title">
            <div className="sbx-home-title-row">
              <h1>{firstName ? `Your sandbox, ${firstName}` : 'Your sandbox'}</h1>
              <SandboxStatePill loading={loading} state={state} waking={waking} />
              <PlanPill sandbox={sandbox} />
              <SandboxTypePill sandbox={sandbox} />
            </div>
            <p>
              {sandbox?.idleMinutes
                ? `Sleeps after ${sandbox.idleMinutes} minutes without use, wakes on the next request.`
                : 'A private machine for your projects.'}
            </p>
          </div>
          <div className="sbx-home-actions">
            {refreshButton}
            {wakeButton}
            <Button onClick={startNewProject} type="primary">+ New project</Button>
            <ManageSandboxMenu onChanged={refresh} />
            {sleepError && <span className="sbx-action-error" role="alert">{sleepError}</span>}
          </div>
        </header>

        {error && error.status === 401 && (
          <div className="sbx-card sbx-home-error" role="alert">
            Your Flow session has expired, so your sandbox can’t be shown. Sign in again to continue; your projects are safe.
            <Button onClick={() => window.location.assign(`/bff/login?redirect_uri=${encodeURIComponent(window.location.href)}`)} size="small" type="primary">Sign in</Button>
          </div>
        )}
        {error && error.status !== 401 && (
          <div className="sbx-card sbx-home-error" role="alert">
            Your sandbox could not be reached ({error.message}). Your projects are safe; try again in a moment.
            <Button onClick={refresh} size="small">Retry</Button>
          </div>
        )}

        {/* Why the sandbox (or Flow) makes the person wait: waking with its step and time, a refused start, Flow being
            updated, their connection (SandboxWaking); nothing while it runs. */}
        <SandboxWaking compact watch={loading} />

        {warnings.length > 0 && (
          <div className="sbx-card sbx-home-warnings" role="status">
            <strong>Close to your plan’s limits</strong>
            <ul>{warnings.map((warning) => <li key={warning.key}>{warning.message}</li>)}</ul>
            <PlanChangeButton fallback={<span>To change your plan, ask your sandbox admin.</span>} />
          </div>
        )}

        <LiveNow onOpenProject={openProject} you={liveYou} />
        <SinceYouLeft onOpenProject={openProject} projects={recent} you={liveYou} />

        {settings && !settings.home?.checklistDismissed && (
          <GettingStarted
            chatAvailable={chatAvailable}
            onAction={checklistAction}
            onDismiss={hideChecklist}
            projectCount={recent.length}
            sandbox={sandbox}
            settings={settings}
          />
        )}

        <UsageCard
          loading={loading}
          onConnect={() => navigate('/welcome')}
          onOpenTool={setTool}
          onPlanRequests={setPlanRequests}
          planRequests={planRequests}
          recent={recent}
          sandbox={sandbox}
          stats={myStats}
        />

        <RunningNow
          loading={loading}
          onChanged={refresh}
          onOpenLogs={openLogs}
          onOpenProject={openProject}
          onStopAll={stopAll}
          sandbox={sandbox}
          sleepAction={sleepButton}
          stoppingAll={stoppingAll}
          tenantId={tenantId}
        />

        {recent.length > 0 && (
          <section aria-labelledby="sbx-recent" className="sbx-home-plain">
            <div className="sbx-home-section-head">
              <h2 className="sbx-label" id="sbx-recent">Recent projects</h2>
              <button className="sbx-link" onClick={() => navigate('/projects')} type="button">See all</button>
            </div>
            <div className="sbx-recent">
              {recent.map((project) => (
                <button className="sbx-card sbx-recent-card" key={project._id} onClick={() => openProject(project._id)} type="button">
                  <strong title={project.name}>{project.name}</strong>
                  <span>
                    {formatRelativeTime(project.visitedAt || project.updatedAt || project.createdAt)}
                    {project.createdWith ? <> · <VendorName name={project.createdWith} size={12} /></> : ''}
                  </span>
                  <span className={`sbx-pill ${runningByProject[project._id] ? 'sbx-pill-running' : ''}`}>
                    {runningByProject[project._id] ? `${runningByProject[project._id]} running` : 'Stopped'}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        <nav aria-label="More with Flow" className="sbx-more">
          <span>More with Flow:</span>
          {chatOn
            ? <a href="/chat" onClick={go('/chat')}>Open chat</a>
            : chatAvailable && <a href="/features/chat" onClick={go('/features/chat')}>Turn on Chat</a>}
          <a href="/brain" onClick={go('/brain')}>Brain</a>
          <span className="sbx-more-item">
            <a href="/features/connections" onClick={go('/features/connections')}>Connections</a>
            <span className="sbx-pill sbx-pill-paid">Paid add-ons</span>
            {connectionsIncluded ? <span className="sbx-more-note">{connectionsIncluded} in your plan</span> : null}
          </span>
          <a href="/welcome" onClick={go('/welcome')}>Connect another AI tool</a>
        </nav>
      </div>
      {modals}
    </div>
  );
}

SandboxHome.propTypes = {
  user: PropTypes.object,
  selectedTenant: PropTypes.object,
  onNewProject: PropTypes.func,
  // ({ sandboxSummary, focusPrompt }) => the prompt Home with the sandbox under it (App.jsx); used while Chat is on.
  renderChatHome: PropTypes.func,
};
